import { format } from 'date-fns';
import { ref, update } from 'firebase/database';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import { audioApi, taskApi, videoApi } from 'api';
import { HW, ICL, WorkType } from 'shared/types';

import { imgApi } from './img-api';
import { database, firestore } from '../Common/Firebase';
import { Exam, ExamStatus, PersonOption } from '../shared/types/works';

const colName = 'Works';

const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

const threeMinutesInMs = 3 * 60 * 1000; // use for test deleting icls for ever

const colRef = collection(firestore, colName);
const getDocRef = (ICLId: ICL['id']) => doc(firestore, colName, ICLId);

async function addICL(icl: ICL) {
  const newId = uuidv4();
  const newICL = { ...icl, id: newId }; // add or overwrite ICL ID

  const docRef = getDocRef(newId);

  await setDoc(docRef, newICL);

  return newICL;
}

async function updateICL(icl: ICL) {
  const docRef = getDocRef(icl.id);

  await updateDoc(docRef, { ...icl });
}

async function removeICL(id: ICL['id']) {
  // just changing status for future delete it can be restored
  const docRef = getDocRef(id);
  await updateDoc(docRef, { deleted: true, deletedAt: new Date().toISOString() });
}

async function removeDeletedIcls() {
  try {
    // Get all documents that have the status deleted: true
    const docsQuery = query(colRef, where('deleted', '==', true));
    const querySnapshot = await getDocs(docsQuery);

    console.log('works deletios');

    // We check every document
    querySnapshot.forEach(async docSnapshot => {
      const docData = docSnapshot.data();
      const deleteTime = new Date(docData.deletedAt).getTime();
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - deleteTime;

      // If more than a week has passed
      if (elapsedTime >= oneWeekInMs) {
        console.log(`Document ${docSnapshot.id} is ready for full deletion.`);

        const id = docSnapshot.id;
        const tasks = await taskApi.getICLTasks(id);
        await taskApi.removeICLTasks(id, tasks);

        for (const task of tasks) {
          if (task.audioUrl) await audioApi.removeAudio(task.audioUrl, id, task.id);
          if (task.videoUrl) await videoApi.removeVideo(task.videoUrl, id, task.id);

          if (task.taskPhotos) {
            for (const photo of task.taskPhotos) {
              await imgApi.removeImg(photo, id, task.id, task.taskPhotos, 'taskPhotos');
            }
          }

          if (task.answersPhotos) {
            for (const photo of task.answersPhotos) {
              await imgApi.removeImg(photo, id, task.id, task.answersPhotos, 'answersPhotos');
            }
          }

          if (task.editedAnswerPhotos) {
            for (const photo of task.editedAnswerPhotos) {
              await imgApi.removeImg(photo, id, task.id, task.editedAnswerPhotos, 'editedAnswerPhotos');
            }
          }
        }

        await deleteDoc(getDocRef(id));
        console.log(`Document ${id} has been fully deleted.`);
      } else {
        console.log(`Document ${docSnapshot.id} is not yet ready for full deletion.`);
      }
    });
  } catch (error) {
    console.error('Failed to check or delete documents:', error);
  }
}

async function restoreICL(id: ICL['id']) {
  const docRef = getDocRef(id);

  const docSnapshot = await getDoc(docRef);

  if (!docSnapshot.exists()) {
    throw new Error('Document not found.');
  }

  const docData = docSnapshot.data();

  if (docData?.deleted !== true) {
    throw new Error('Document is not marked as deleted.');
  }

  await updateDoc(docRef, { deleted: false, deletedAt: null });

  console.log(`ICL with ID ${id} has been restored.`);
}

interface SubscribeToAdminICLsParams {
  setNewICLs: (ICLs: ICL[]) => void;
  studentFilter?: PersonOption | null;
  teacherFilter?: PersonOption | null;
  subjectFilter?: string | null;
  classFilter?: string | null;
}

function subscribeToAdminICLs({
  setNewICLs,
  classFilter,
  subjectFilter,
  teacherFilter,
  studentFilter,
}: SubscribeToAdminICLsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.icl), where('isConsideration', '==', false));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (teacherFilter) {
    queryRef = query(queryRef, where('teacherEmail', '==', teacherFilter.email));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

function subscribeToAdminExams({
  setNewICLs,
  classFilter,
  subjectFilter,
  teacherFilter,
  studentFilter,
}: SubscribeToAdminICLsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.exam), where('isConsideration', '==', false));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (teacherFilter) {
    queryRef = query(queryRef, where('teacherEmail', '==', teacherFilter.email));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

function subscribeToAdminConsiderationICLs({
  setNewICLs,
  classFilter,
  subjectFilter,
}: Omit<SubscribeToAdminICLsParams, 'studentFilter' | 'teacherFilter'>) {
  let queryRef = query(colRef, where('type', '==', WorkType.icl), where('isConsideration', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

function subscribeToAdminConsiderationExams({
  setNewICLs,
  classFilter,
  subjectFilter,
}: Omit<SubscribeToAdminICLsParams, 'studentFilter' | 'teacherFilter'>) {
  let queryRef = query(colRef, where('type', '==', WorkType.exam), where('isConsideration', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

interface SubscribeToTeacherICLsParams {
  setNewICLs: (ICLs: ICL[]) => void;
  teacherEmail: string;
  isActive?: boolean;
  subjectFilter?: string | null;
  classFilter?: string | null;
  studentFilter?: PersonOption | null;
  myICLs?: boolean;
  studentsWorks?: boolean;
  correction?: boolean;
  deleted?: boolean;
}

function subscribeToTeacherICLs({
  setNewICLs,
  teacherEmail,
  isActive = true,
  classFilter,
  subjectFilter,
  studentFilter,
  studentsWorks,
  myICLs,
  correction,
}: SubscribeToTeacherICLsParams) {
  let queryRef = query(
    colRef,
    where('type', '==', WorkType.icl),
    where('teacherEmail', '==', teacherEmail),
    where('isActive', '==', isActive),
  );

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  //getDocs(queryRef).then(onMySnapshot);
  return onSnapshot(queryRef, snapshot => {
    let ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));

    if (studentsWorks) ICLs = ICLs.filter(icl => icl?.studentEmail);

    if (myICLs) ICLs = ICLs.filter(icl => icl?.teacherEmail === teacherEmail && !icl?.studentEmail);

    if (correction) ICLs = ICLs.filter(icl => icl?.correction);
    else ICLs = ICLs.filter(icl => !icl?.correction);

    console.log('test');
    setNewICLs(ICLs);
  });
}

function subscribeToTeacherExams({
  setNewICLs,
  teacherEmail,
  isActive = true,
  classFilter,
  subjectFilter,
  studentFilter,
  studentsWorks,
  myICLs,
  deleted,
}: SubscribeToTeacherICLsParams) {
  let queryRef = query(
    colRef,
    where('type', '==', WorkType.exam),
    where('teacherEmail', '==', teacherEmail),
    where('isActive', '==', isActive),
  );

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  return onSnapshot(queryRef, snapshot => {
    let ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));

    if (deleted === true) {
      ICLs = ICLs.filter(icl => icl.deleted === true);
    }

    if (deleted === false) {
      ICLs = ICLs.filter(icl => icl.deleted !== true);
    }

    if (studentsWorks) {
      ICLs = ICLs.filter(icl => icl.studentEmail);
    }

    if (myICLs) {
      ICLs = ICLs.filter(icl => icl.teacherEmail === teacherEmail && !icl.studentEmail);
    }

    setNewICLs(ICLs);
  });
}

interface SubscribeToTemplateICLsParams {
  setNewICLs: (ICLs: ICL[]) => void;
  subjectFilter?: string | null;
  classFilter?: string | null;
}

function subscribeToTemplateICLs({ setNewICLs, classFilter, subjectFilter }: SubscribeToTemplateICLsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.icl), where('isTemplate', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

function subscribeToTemplateExams({ setNewICLs, classFilter, subjectFilter }: SubscribeToTemplateICLsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.exam), where('isTemplate', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

interface SubscribeToStudentICLsParams {
  setNewICLs: (ICLs: ICL[]) => void;
  studentEmail: string;
}

function subscribeToStudentICLs({ setNewICLs, studentEmail }: SubscribeToStudentICLsParams) {
  const queryRef = query(
    colRef,
    where('type', '==', WorkType.icl),
    where('studentEmail', '==', studentEmail),
    where('isActive', '==', true),
  );

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

function subscribeToStudentExams({ setNewICLs, studentEmail }: SubscribeToStudentICLsParams) {
  const queryRef = query(
    colRef,
    where('type', '==', WorkType.exam),
    where('studentEmail', '==', studentEmail),
    where('isActive', '==', true),
  );

  return onSnapshot(queryRef, snapshot => {
    const ICLs: ICL[] = [];
    snapshot.forEach(doc => ICLs.push(doc.data() as ICL));
    setNewICLs(ICLs);
  });
}

interface SubscribeToICLByIdParams {
  ICLId: ICL['id'];
  setNewICL: (ICL: ICL) => void;
}

function subscribeToICL({ ICLId, setNewICL }: SubscribeToICLByIdParams) {
  const docRef = getDocRef(ICLId);

  return onSnapshot(docRef, snapshot => {
    setNewICL(snapshot.data() as ICL);
  });
}

async function getICL(ICLId: ICL['id']) {
  const docRef = getDocRef(ICLId);
  const docSnap = await getDoc(docRef);
  const ICL = docSnap.data() as ICL;

  return ICL;
}

async function copyICLToTeacher(ICL: ICL, teacherEmail: ICL['teacherEmail']) {
  const prevICLId = ICL.id;
  const newICL: ICL = {
    ...ICL,
    parentId: prevICLId,
    teacherEmail,
    createdAt: Date.now(),
    isTemplate: false,
    isActive: true,
    checked: false,
    completed: false,
  };

  const newICLId = await addICL(newICL);
  await taskApi.copyICLTasks(prevICLId, newICLId.id);
}

async function copyICLToAdmin(ICL: ICL) {
  const prevICLId = ICL.id;
  const newICL: ICL = {
    ...ICL,
    parentId: prevICLId,
    isTemplate: true,
    isActive: true,
    createdAt: Date.now(),
    teacherEmail: '',
    studentEmail: '',
    checked: false,
    completed: false,
  };

  const newICLId = await addICL(newICL);
  await taskApi.copyICLTasks(prevICLId, newICLId.id);
}

async function sendICLToStudents(ICL: ICL | HW | Exam, studentsEmails: string[]) {
  const prevICLId = ICL.id;
  const batch = writeBatch(firestore);
  const newICLIds: string[] = [];

  studentsEmails.forEach(email => {
    const newId = uuidv4();
    const newICL: ICL | HW | Exam = {
      ...ICL,
      parentId: prevICLId,
      createdAt: Date.now(),
      id: newId,
      studentEmail: email,
    }; // overwrite ICL ID

    if (ICL.type === WorkType.exam) (newICL as Exam).status = ExamStatus.sended;
    const docRef = getDocRef(newId);
    batch.set(docRef, newICL);
    newICLIds.push(newId);
  });

  studentsEmails.forEach((email, index) => {
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    const notificationId = uuidv4();

    const notificationData = {
      id: notificationId + email,
      idItem: email,
      subject: ICL.subject,
      name: '',
      time: formattedTime,
      type: ICL.type === WorkType.hw ? 'hwToStudent' : ICL.type === WorkType.exam ? 'examToStudent' : 'iclToStudent',
      iclId: newICLIds[index],
      viewed: true,
    };

    update(ref(database, `Students/${email}/notification`), {
      [notificationId + email]: notificationData,
    });
  });

  await taskApi.prepareTasksForCopying(prevICLId, batch, newICLIds);
  await batch.commit();
}

export const iclApi = {
  removeDeletedIcls,
  getICL,
  addICL,
  removeICL,
  updateICL,
  subscribeToAdminICLs,
  subscribeToAdminExams,
  subscribeToTeacherICLs,
  subscribeToTeacherExams,
  subscribeToStudentICLs,
  subscribeToStudentExams,
  subscribeToTemplateICLs,
  subscribeToTemplateExams,
  subscribeToAdminConsiderationICLs,
  subscribeToAdminConsiderationExams,
  subscribeToICL,
  copyICLToTeacher,
  copyICLToAdmin,
  sendICLToStudents,
  restoreICL,
};
